import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Card from 'react-bootstrap/Card';
import resumeImage from './HomeResumeImg.webp';
import CardGroup from 'react-bootstrap/CardGroup';
import { FiCheckCircle, FiFileText, FiTrendingUp, FiAward, FiZap, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const Home = () => {
  const [showContent, setShowContent] = useState({
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
    7: false,
  });

  // Function to toggle content visibility
  const toggleContent = (index) => {
    setShowContent((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div className="home-page-container">
      {/* Hero Section */}
      <div className="home-container">
        <div className="content">
          <div className="hero-badge">
            <FiAward className="badge-icon" />
            <span>Professional Resume Builder</span>
          </div>
          <h1 className="h1-tag">
            Fast and Simple Online 
            <span className="highlight-text"> Free ATS Resume Builder</span>
          </h1>
          <p className="hero-description">
            Create an ATS-friendly resume for free. Optimize your resume to pass through Applicant Tracking
            Systems and increase your chances of landing a job
          </p>
          <Link to="/create-resume" className="cta-button">
            <span>Start Creating Resume</span>
            <FiZap className="cta-icon" />
          </Link>
          
          <div className="trust-indicators">
            <div className="trust-item">
              <FiCheckCircle className="trust-icon" />
              <span>100% Free</span>
            </div>
            <div className="trust-divider"></div>
            <div className="trust-item">
              <FiFileText className="trust-icon" />
              <span>ATS Optimized</span>
            </div>
            <div className="trust-divider"></div>
            <div className="trust-item">
              <FiTrendingUp className="trust-icon" />
              <span>Easy to Use</span>
            </div>
          </div>
        </div>

        <div className="image-container">
          <div className="image-decoration"></div>
          <img src={resumeImage} alt="Resume Builder Image" />
          <div className="floating-badge badge-1">
            <FiCheckCircle />
            <span>ATS Friendly</span>
          </div>
          <div className="floating-badge badge-2">
            <FiAward />
            <span>Professional</span>
          </div>
        </div>
      </div>

      {/* Cards Section */}
      <div className="section-2">
        <CardGroup className="custom-card-group">
          <Card className="info-card card-1">
            <div className="card-accent"></div>
            <Card.Body>
              <div className="card-icon-wrapper">
                <FiFileText className="card-icon" />
              </div>
              <Card.Title>
                <h2>What is an ATS Resume?</h2>
              </Card.Title>
              <Card.Text>
                <p>
                  An <strong>ATS resume</strong> is a resume specifically formatted to pass through Applicant Tracking Systems 
                  used by recruiters and employers. These systems are designed to screen, rank, 
                  and sort resumes based on keywords, skills, and other job-related criteria.
                  If your resume isn't formatted properly or lacks the right keywords,
                  it could be automatically rejected, even if you're highly qualified.
                </p>
              </Card.Text>
            </Card.Body>
          </Card>

          <Card className="info-card card-2">
            <div className="card-accent"></div>
            <Card.Body>
              <div className="card-icon-wrapper">
                <FiTrendingUp className="card-icon" />
              </div>
              <Card.Title>
                <h2>Why Do You Need an ATS Resume?</h2>
              </Card.Title>
              <Card.Text>
                <p>
                  Most companies, especially large organizations, use ATS to manage job applications.
                  According to research, over <strong>75% of resumes</strong> are rejected by ATS 
                  before a human ever sees them. This means that even a small formatting error or
                  missing keyword could cost you the job. By creating an ATS-optimized resume, 
                  you can increase your chances of being noticed and landing an interview.
                </p>
              </Card.Text>
            </Card.Body>
          </Card>
        </CardGroup>
      </div>

      {/* Introduction Section */}
      <div className="section-1">
        <div className="section-header-decoration"></div>
        <h2>How to Create an Applicant Tracking System-Friendly Resume</h2>
        <p>
          In today's competitive job market, having an ATS-friendly resume is essential for getting noticed
          by employers. An <strong>Applicant Tracking System (ATS)</strong> is
          software that scans resumes to filter the most relevant candidates before they even 
          reach the hiring manager. To make sure your resume passes through the ATS, 
          it needs to be well-optimized with the right structure, format, and keywords. 
          In this blog, we'll explore what a free ATS resume is, why it's important, 
          and how you can create one that stands out.
        </p>
      </div>

      {/* Tips Section */}
      <div className="section-3">
        <div className="tips-container">
          <div className="tips-header">
            <h2>Key Tips for Creating a Free ATS Resume</h2>
            <div className="tips-subtitle">Follow these essential guidelines to optimize your resume</div>
          </div>

          <ul className="tips-list">
            <li className="tip-item">
              <div className="tip-header" onClick={() => toggleContent(1)}>
                <div className="tip-number">01</div>
                <strong className="tip-title">Use Standard Fonts and Formatting</strong>
                <button className="toggle-btn">
                  {showContent[1] ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </div>
              {showContent[1] && (
                <div className="tip-content">
                  <p>
                    Stick to simple fonts like Arial, Calibri, or Times New Roman.
                    Avoid using fancy templates, graphics, or unusual font sizes.
                    ATS systems prefer plain text, so fancy designs might not be readable by the system.
                  </p>
                </div>
              )}
            </li>

            <li className="tip-item">
              <div className="tip-header" onClick={() => toggleContent(2)}>
                <div className="tip-number">02</div>
                <strong className="tip-title">Optimize for Keywords</strong>
                <button className="toggle-btn">
                  {showContent[2] ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </div>
              {showContent[2] && (
                <div className="tip-content">
                  <p>
                    Tailor your resume to the job description by including relevant keywords.
                    Focus on the skills, qualifications, and experiences that match the job posting.
                    Keywords like "project management," "data analysis," or "software development"
                    (depending on the role) will help your resume rank higher in the ATS.
                  </p>
                </div>
              )}
            </li>

            <li className="tip-item">
              <div className="tip-header" onClick={() => toggleContent(3)}>
                <div className="tip-number">03</div>
                <strong className="tip-title">Use a Simple Resume Layout</strong>
                <button className="toggle-btn">
                  {showContent[3] ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </div>
              {showContent[3] && (
                <div className="tip-content">
                  <p>
                    Ensure your resume has clear sections with standard headings like "Work Experience," "Education," and "Skills."
                    ATS software relies on these headings to understand your qualifications. Avoid tables, columns, and non-standard
                    sections that might confuse the system.
                  </p>
                </div>
              )}
            </li>

            <li className="tip-item">
              <div className="tip-header" onClick={() => toggleContent(4)}>
                <div className="tip-number">04</div>
                <strong className="tip-title">Choose the Right File Format</strong>
                <button className="toggle-btn">
                  {showContent[4] ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </div>
              {showContent[4] && (
                <div className="tip-content">
                  <p>
                    Always submit your resume in either a .docx or .pdf format unless specified otherwise in the job listing.
                    Some ATS systems struggle with parsing .pdf files, so when in doubt, stick to .docx for better readability.
                  </p>
                </div>
              )}
            </li>

            <li className="tip-item">
              <div className="tip-header" onClick={() => toggleContent(5)}>
                <div className="tip-number">05</div>
                <strong className="tip-title">List Skills and Experiences Properly</strong>
                <button className="toggle-btn">
                  {showContent[5] ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </div>
              {showContent[5] && (
                <div className="tip-content">
                  <p>Use bullet points to describe your achievements and responsibilities in each role. Be concise but detailed. For instance:</p>
                  <ul className="nested-list">
                    <li>Managed a team of 10 developers to successfully complete software projects on time.</li>
                    <li>Analyzed data to improve marketing strategies, resulting in a 20% increase in lead generation.</li>
                  </ul>
                </div>
              )}
            </li>

            <li className="tip-item">
              <div className="tip-header" onClick={() => toggleContent(6)}>
                <div className="tip-number">06</div>
                <strong className="tip-title">Use Online Tools for Free ATS Resume</strong>
                <button className="toggle-btn">
                  {showContent[6] ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </div>
              {showContent[6] && (
                <div className="tip-content">
                  <p>
                    There are many free tools available online that can help you create an ATS-friendly resume.
                    Websites like Jobscan, Resumake, and NovoResume provide free ATS resume templates and keyword optimization tips.
                    These tools allow you to scan your resume against job descriptions to ensure it's ATS-ready.
                  </p>
                </div>
              )}
            </li>

            <li className="tip-item">
              <div className="tip-header" onClick={() => toggleContent(7)}>
                <div className="tip-number">07</div>
                <strong className="tip-title">Check for ATS Compliance</strong>
                <button className="toggle-btn">
                  {showContent[7] ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </div>
              {showContent[7] && (
                <div className="tip-content">
                  <p>
                    After crafting your resume, use an ATS checker tool to scan it and get insights into its ATS-friendliness.
                    These tools highlight areas for improvement, suggest keywords, and ensure your formatting is suitable for ATS software.
                  </p>
                </div>
              )}
            </li>
          </ul>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="final-cta-section">
        <div className="cta-content-wrapper">
          <h2>Ready to Build Your Perfect Resume?</h2>
          <p>Start creating your ATS-optimized resume today and increase your chances of landing your dream job</p>
          <Link to="/create-resume" className="cta-button-large">
            <span>Create Your Resume Now</span>
            <FiZap />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Home;